import {
  Activity,
  CreditCard,
  FilePlus2,
  KeyRound,
  MousePointerClick,
  Send,
  Sparkles,
  UserPlus,
  type LucideIcon,
} from 'lucide-react'
import { Alert, Drawer, EmptyState, Spinner, StatCard, formatCurrency, formatDate, formatNumber } from '@/ui'
import { useApiFetch } from '@/hooks/useApiFetch'
import { CHANNEL_LABELS, PRODUCT_TYPE_LABELS } from './labels'
import { formatDurationKo } from './duration'
import type { JourneyEvent, JourneyEventKind, PaymentJourneyResult, PaymentJourneyUser } from './types'
import styles from './JourneyDrawer.module.css'

const EVENT_CONFIG: Record<JourneyEventKind, { label: string; icon: LucideIcon }> = {
  first_touch: { label: '첫 유입', icon: MousePointerClick },
  signup: { label: '회원 가입', icon: UserPlus },
  invitation_created: { label: '청첩장 생성', icon: FilePlus2 },
  invitation_published: { label: '청첩장 발행', icon: Send },
  ai_activity_summary: { label: 'AI 활동', icon: Sparkles },
  other_payment: { label: '다른 결제', icon: CreditCard },
  payment: { label: '대상 결제', icon: CreditCard },
  activation: { label: '활성화', icon: KeyRound },
}

interface JourneyDrawerProps {
  open: boolean
  paymentId: string | null
  onClose: () => void
}

function getString(event: JourneyEvent, key: string): string | null {
  const value = event[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

function getNumber(event: JourneyEvent, key: string): number | null {
  const value = event[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function getBoolean(event: JourneyEvent, key: string): boolean {
  return event[key] === true
}

function getMarketingLinkLabel(event: JourneyEvent): string | null {
  const value = event.marketingLink
  if (!value || typeof value !== 'object') return null
  const label = (value as { label?: unknown }).label
  return typeof label === 'string' && label.length > 0 ? label : null
}

function formatFirstTouch(user: PaymentJourneyUser): string {
  const utm = [user.utmSource, user.utmMedium, user.utmCampaign, user.utmContent].filter(Boolean).join(' / ')
  if (utm) return utm
  if (user.referrer) return user.referrer
  return '(direct)'
}

function eventTitle(event: JourneyEvent): string {
  if (event.kind === 'ai_activity_summary') {
    return getString(event, 'phase') === 'after_pay' ? '결제 후 AI 활동' : '결제 전 AI 활동'
  }
  return EVENT_CONFIG[event.kind].label
}

function eventDetails(event: JourneyEvent, user: PaymentJourneyUser | null): string[] {
  const details: string[] = []

  if (event.kind === 'first_touch') {
    const utm = [
      getString(event, 'utmSource'),
      getString(event, 'utmMedium'),
      getString(event, 'utmCampaign'),
      getString(event, 'utmContent'),
    ].filter(Boolean).join(' / ')
    const marketingLink = getMarketingLinkLabel(event)
    const landingPath = getString(event, 'landingPath')
    const referrer = getString(event, 'referrer')

    if (utm) details.push(utm)
    if (marketingLink) details.push(`마케팅 링크: ${marketingLink}`)
    if (landingPath) details.push(`랜딩: ${landingPath}`)
    if (referrer) details.push(`Referrer: ${referrer}`)
  }

  if (event.kind === 'signup' && user) {
    details.push(user.name ?? user.email)
  }

  if (event.kind === 'invitation_created' || event.kind === 'invitation_published') {
    const invitationId = getString(event, 'invitationId')
    if (invitationId) details.push(`청첩장 ${invitationId.slice(0, 8)}`)
  }

  if (event.kind === 'ai_activity_summary') {
    const count = getNumber(event, 'count')
    const creditsUsed = getNumber(event, 'creditsUsed')
    const firstAt = getString(event, 'firstAt')
    const lastAt = getString(event, 'lastAt')
    if (count != null) details.push(`${formatNumber(count)}회`)
    if (creditsUsed != null) details.push(`${formatNumber(creditsUsed)} 크레딧`)
    if (firstAt && lastAt) {
      details.push(`${formatDate(firstAt, 'M.d HH:mm')}~${formatDate(lastAt, 'M.d HH:mm')}`)
    }
  }

  if (event.kind === 'payment' || event.kind === 'other_payment') {
    const type = getString(event, 'type')
    const channel = getString(event, 'channel')
    const status = getString(event, 'status')
    const netAmount = getNumber(event, 'netAmount')
    const inflowPath = getString(event, 'inflowPath')

    if (type) details.push(PRODUCT_TYPE_LABELS[type] ?? type)
    if (channel) details.push(CHANNEL_LABELS[channel] ?? channel)
    if (status) details.push(status)
    if (netAmount != null) details.push(`순매출 ${formatCurrency(netAmount)}`)
    if (getBoolean(event, 'paidAtApprox')) details.push('결제 시각 근사값')
    if (inflowPath) details.push(`유입경로: ${inflowPath}`)
  }

  if (event.kind === 'activation') {
    const code = getString(event, 'code')
    const type = getString(event, 'type')
    const smartstoreOrderId = getString(event, 'smartstoreOrderId')
    if (code) details.push(`코드 ${code}`)
    if (type) details.push(type)
    if (smartstoreOrderId) details.push(`주문 ${smartstoreOrderId}`)
  }

  return details
}

function PaymentSummary({ data }: { data: PaymentJourneyResult }) {
  const { payment, user } = data
  return (
    <section className={styles.summaryPanel}>
      <div>
        <p className={styles.sectionLabel}>결제</p>
        <dl className={styles.summaryGrid}>
          <div>
            <dt>상품</dt>
            <dd>{PRODUCT_TYPE_LABELS[payment.type] ?? payment.type}</dd>
          </div>
          <div>
            <dt>채널</dt>
            <dd>{CHANNEL_LABELS[payment.channel] ?? payment.channel}</dd>
          </div>
          <div>
            <dt>순매출</dt>
            <dd>{formatCurrency(payment.netAmount)}</dd>
          </div>
          <div>
            <dt>결제일시</dt>
            <dd>
              {formatDate(payment.paidAt, 'yy.M.d HH:mm')}
              {payment.paidAtApprox ? ' (근사)' : ''}
            </dd>
          </div>
        </dl>
      </div>

      {user && (
        <div>
          <p className={styles.sectionLabel}>사용자</p>
          <dl className={styles.summaryGrid}>
            <div>
              <dt>이름</dt>
              <dd>{user.name ?? '-'}</dd>
            </div>
            <div>
              <dt>이메일</dt>
              <dd>{user.email}</dd>
            </div>
            <div>
              <dt>가입일시</dt>
              <dd>{formatDate(user.signupAt, 'yy.M.d HH:mm')}</dd>
            </div>
            <div>
              <dt>첫 유입</dt>
              <dd>{formatFirstTouch(user)}</dd>
            </div>
          </dl>
          {user.marketingLink && (
            <p className={styles.summaryNote}>매칭 마케팅 링크: {user.marketingLink.label}</p>
          )}
        </div>
      )}
    </section>
  )
}

function DeliberationStats({ data }: { data: PaymentJourneyResult }) {
  const deliberation = data.deliberation
  if (!deliberation) return null

  return (
    <section>
      <p className={styles.sectionLabel}>고민 지표</p>
      <div className={styles.statGrid}>
        <StatCard label="가입→결제 소요" value={formatDurationKo(deliberation.signupToPayMs)} />
        <StatCard label="청첩장 생성→결제" value={formatDurationKo(deliberation.invitationToPayMs)} />
        <StatCard
          label="결제 전 AI 활동"
          value={`${formatNumber(deliberation.aiActivitiesBeforePay)}회`}
          change={`${formatNumber(deliberation.aiCreditsUsedBeforePay)} 크레딧`}
        />
        <StatCard label="활동일수" value={`${formatNumber(deliberation.activeDays)}일`} />
      </div>
    </section>
  )
}

function Timeline({ data }: { data: PaymentJourneyResult }) {
  return (
    <section>
      <p className={styles.sectionLabel}>타임라인</p>
      <ol className={styles.timeline}>
        {data.timeline.map((event, index) => {
          const config = EVENT_CONFIG[event.kind] ?? { label: event.kind, icon: Activity }
          const Icon = config.icon
          const details = eventDetails(event, data.user)
          return (
            <li key={`${event.kind}-${event.at}-${index}`} className={styles.timelineItem}>
              <span className={styles.timelineIcon}>
                <Icon width={16} height={16} />
              </span>
              <div className={styles.timelineContent}>
                <div className={styles.timelineHeader}>
                  <p className={styles.timelineTitle}>{eventTitle(event)}</p>
                  <time className={styles.timelineTime}>{formatDate(event.at, 'yy.M.d HH:mm')}</time>
                </div>
                {details.length > 0 && (
                  <p className={styles.timelineMeta}>{details.join(' · ')}</p>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </section>
  )
}

function InactiveOrderState({ data }: { data: PaymentJourneyResult }) {
  return (
    <div className={styles.inactiveState}>
      <EmptyState
        title="미활성화 스마트스토어 주문 — 여정 추적 불가"
        description="사용자 계정과 연결되지 않은 주문이라 가입·청첩장·AI 활동을 재구성할 수 없습니다."
      />
      {data.payment.inflowPath && (
        <dl className={styles.inflowBox}>
          <div>
            <dt>스마트스토어 유입경로</dt>
            <dd>{data.payment.inflowPath}</dd>
          </div>
          {data.payment.inflowPathAdd && (
            <div>
              <dt>추가 유입경로</dt>
              <dd>{data.payment.inflowPathAdd}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  )
}

export function JourneyDrawer({ open, paymentId, onClose }: JourneyDrawerProps) {
  const { data, error, isLoading } = useApiFetch<PaymentJourneyResult>(
    open && paymentId ? `/api/stats/payment-journey/${encodeURIComponent(paymentId)}` : null,
  )

  return (
    <Drawer open={open} onClose={onClose} title="결제 여정" size="lg">
      {error && <Alert variant="danger">불러오지 못했습니다: {error}</Alert>}

      {isLoading && !data ? (
        <div className={styles.center}><Spinner /></div>
      ) : data ? (
        <div className={styles.body}>
          <PaymentSummary data={data} />
          {data.user === null ? (
            <InactiveOrderState data={data} />
          ) : (
            <DeliberationStats data={data} />
          )}
          <Timeline data={data} />
        </div>
      ) : null}
    </Drawer>
  )
}
